import { getUrlWithVariant, ProductForm } from '@shopify/theme-product-form';
import { formatMoney } from '@shopify/theme-currency';

import '../../styles/components/product-form.scss';

import { addToCartForm } from '../utils/cart';

class ProductFormElement extends HTMLElement {
  constructor() {
    super();

    this.productWrapper = this.closest("[data-product-wrapper]");

    this.priceWrapperElement = this.productWrapper.querySelector(".price");
    this.priceElement = Array.from(this.priceWrapperElement.querySelectorAll("[data-price]"));
    this.comparePriceElement = Array.from(this.priceWrapperElement.querySelectorAll("[data-compare-at-price]"));

    this.skuContainer = this.productWrapper.querySelector("[data-product-sku-container]");
    this.skuElement = this.productWrapper.querySelector("[data-product-sku]");

    this.imageSwiper = this.productWrapper.querySelector("[data-product-image-swiper]");
    this.thumbsSwiper = this.productWrapper.querySelector("[data-product-thumbs-swiper]");

    this.allColorsLabel = Array.from(this.querySelectorAll('[data-color-label]'));
    this.allColorsLabel?.forEach(color => {
      color.addEventListener('change', this.onProductCardChange.bind(this));
    });

    this.badge = this.productWrapper.querySelector('[data-badge]');

    this.productHandle = this.getAttribute('product-handle');
    this.productFormElement = this.querySelector('form');
    this.submitButton = this.querySelector('[data-submit-product]');
    this.selectId = this.querySelector('[name="id"]');

    this.allSwatches = Array.from(this.querySelectorAll('[data-option-position]'));

    this.initializeForm();

    this.handleGroupCollectionSwatch();
    this.productFormElement.addEventListener('submit', this.submitProductForm.bind(this));
  }

  updateBadge(variant) {
    const badge = this.productWrapper.querySelector('[data-product-badge]');
    const badgeInner = badge.querySelector('[data-product-badge-inner]');
    const customBadge = badge.querySelector('[data-custom-product-badge]');
    const showSalePercentage = badge.dataset.showSalePercentage;

    function badgeUpdate(label) {
      label = label.toLowerCase();

      if (label.includes("%") || label.includes("sale")) {
        customBadge?.classList.add('hide');
        badgeInner.innerHTML = label;
        badgeInner.classList.remove('hide');
        label = "sale";
      } else if (label.toLowerCase().includes("épuisé") || label.toLowerCase().includes("sold")) {
        customBadge?.classList.add('hide');
        badgeInner.innerHTML = theme.strings.product.soldOut;
        badgeInner.classList.remove('hide');
        label = "sold-out";
      } else if (badge.dataset.preset && badge.dataset.preset.trim() != "") {
        badgeInner.classList.add('hide');
        customBadge?.classList.remove('hide');
        label = "custom";
      }

      const badgeClassToRemove = badge.classList[badge.classList.length - 2];
      const badgeClassToAdd = "badge--" + label.toLowerCase().replace(" ", "-");

      badge?.classList.remove(badgeClassToRemove);
      badge?.classList.add(badgeClassToAdd);
      badge?.classList.remove('hide');
      badge?.classList.remove('loading');
    }

    if (variant) {
      badge?.classList.add('loading');

      if (!variant.available) {
        badgeUpdate(theme.strings.product.soldOut);
      } else if (variant.compare_at_price > variant.price) {
        let sale_percentage = "sale";

        if (showSalePercentage) {
          const compareAtPriceDecimaled = variant.compare_at_price / 100;
          const percentOffCalc = (variant.compare_at_price - variant.price) / compareAtPriceDecimaled;
          sale_percentage = Math.round(percentOffCalc)
          sale_percentage = "-" + sale_percentage + "%"
        }
        badgeUpdate(sale_percentage);
      } else if (badge.dataset.preset && badge.dataset.preset.trim() != "") {
        badgeUpdate(badge.dataset.preset.trim());
      } else {
        badge.classList.add('hide');
      }

      badge?.classList.remove('loading');
    } else {
      badge.classList.add('hide');
    }
  }

  async initializeForm() {
    this.product = this.getProductJson();

    this.productForm = new ProductForm(this.productFormElement, this.product, {
      onOptionChange: this.optionsChange.bind(this),
    });

    this.badge ? this.updateBadge(this.productForm.variant()) : null;
  }

  getProductJson() {
    return JSON.parse(this.querySelector('[data-product]').innerHTML);
  }

  async submitProductForm(event) {
    event.preventDefault();

    this.submitButton.classList.add('loading');

    await addToCartForm(this.productFormElement);

    this.submitButton.classList.remove('loading');
  }

  optionsChange(event) {
    const variant = event.dataset.variant;

    this.updateBrowserHistory(variant);
    this.updateMainSelect(variant);
    this.badge ? this.updateBadge(variant) : null;
    this.updateProductPrice(variant);

    if (this.dataset.productCard != "true") {
      this.updateOutOfStockOptions(variant);
    }

    this.updateSku(variant);
    this.updateSelectedOptions(event.currentTarget);
    this.updateProductButton(variant);
    this.updateSlideImages(variant);
  }

  updateBrowserHistory(variant) {
    if (!variant) {
      return;
    }

    const url = getUrlWithVariant(window.location.href, variant.id);
    window.history.replaceState({path: url}, '', url);
  }

  updateMainSelect(variant) {
    if(variant) {
      this.selectId.value = variant.id;
      this.selectId.dispatchEvent(new Event('change'));
    }
  }

  updateProductPrice(variant) {
    this.priceWrapperElement.classList.toggle('hide', !variant);

    if (variant) {
      this.priceElement.forEach(price => price.innerHTML = formatMoney(variant.price, theme.moneyFormat).replace('.00', ''));
      this.comparePriceElement.forEach(compareAtPrice => compareAtPrice.innerHTML = formatMoney(variant.compare_at_price, theme.moneyFormat).replace('.00', ''));

      if (variant.compare_at_price > variant.price) {
        this.priceWrapperElement.classList.add('price--on-sale');
      }
      else {
        this.priceWrapperElement.classList.remove('price--on-sale');
      }

      if (variant.available) {
        this.priceWrapperElement.classList.remove('price--sold-out');
      }
      else {
        this.priceWrapperElement.classList.add('price--sold-out');
      }
    }
  }

  updateSku(variant) {
    if(variant && this.skuElement && variant.sku) {
      this.skuElement.innerHTML = variant.sku;
    }

    if(this.skuContainer) {
      this.skuContainer.classList.toggle('hide', !variant || !variant.sku);
    }
  }

  updateSelectedOptions(targetEvent) {
    const optionValue = targetEvent.value;
    const selectedOptionElement = targetEvent.closest('[data-option-container]').querySelector('[data-selected-option]');
    selectedOptionElement ? selectedOptionElement.innerHTML = optionValue : null;
  }

  updateProductButton(variant) {
    const isQuickBuy = this.submitButton.hasAttribute('data-quick-add');

    if (!isQuickBuy) {
      this.submitButton.innerHTML = "";
    }

    if (variant && !variant.available && isQuickBuy) {
      this.submitButton.disabled = true;
    } else if (variant && variant.available && isQuickBuy) {
      this.submitButton.disabled = false;
    } else if (!variant) {
      this.submitButton.disabled = true;
      this.submitButton.innerHTML = theme.strings.product.unavailable;
    } else if (variant.available) {
      this.submitButton.disabled = false;
      this.submitButton.innerHTML = theme.strings.product.addToCart;
    } else {
      this.submitButton.disabled = true;
      this.submitButton.innerHTML = theme.strings.product.soldOut;
    }
  }

  updateOutOfStockOptions() {
    const $option1 = this.querySelector('input[id^=Option1]:checked') || this.querySelector('select[id^=Option1]');
    const $option2 = this.querySelector('input[id^=Option2]:checked') || this.querySelector('select[id^=Option2]');

    const option1 = $option1 ? $option1.value : null;
    const option2 = $option2 ? $option2.value : null;

    /**
     * we check the status of every swatch.
     *
     * For the first option we just check if any variant is available with that value.
     *
     * For the second option we check if any variant is available with that value and the currently selected first option.
     *
     * For the third option we check if any variant is available with that value and the currently selected first and second options.
     */
    this.allSwatches.forEach(optionInput => {
      if (optionInput.dataset.optionPosition == 1) {
        var availableWithValue = this.product.variants.find(
          variantCompare =>
            variantCompare.option1 === optionInput.value &&
            variantCompare.available
        );
      }
      else if (optionInput.dataset.optionPosition == 2) {
        var availableWithValue = this.product.variants.find(
          variantCompare =>
            variantCompare.option1 === option1 &&
            variantCompare.option2 === optionInput.value &&
            variantCompare.available
        );
      }
      else if (optionInput.dataset.optionPosition == 3) {
        var availableWithValue = this.product.variants.find(
          variantCompare =>
            variantCompare.option1 === option1 &&
            variantCompare.option2 === option2 &&
            variantCompare.option3 === optionInput.value &&
            variantCompare.available
        );
      }

      if(availableWithValue) {
        optionInput.classList.remove('swatches__disabled');
      }
      else {
        optionInput.classList.add('swatches__disabled');
      }
    });
  }

  updateSlideImages(variant) {
    if(variant && this.imageSwiper) {
      /**
       * Go to the variant featured image or first image.
       */
      if(variant.featured_media) {
        const imageId = variant.featured_media.id;
        const imageElement = this.imageSwiper.querySelector(`[data-media-id="${imageId}"]`);
        let imageIndex = [...Array.from(this.imageSwiper.querySelectorAll('.swiper-slide'))].indexOf(imageElement);

        if(imageIndex === -1) {
          imageIndex = 0;
        }

        this.imageSwiper.swiper.slideTo(imageIndex, 500);
      }
    }
  }

  // PRODUCT CARD SWATCH FUNCTIONS
  /*
    This method updates the product card varaint link, price and images on color swatch change
  */
  onProductCardChange(event) {
    const currentVariant = this.productForm.variant();
    const productCard = event.currentTarget.closest('product-card');

    this.updateProductLinks(currentVariant, productCard);
    this.updateFeaturedImage(currentVariant, productCard);
  }

  /*
    This method updates the product card variant link to the current variant link
  */
  updateProductLinks(currentVariant, productCard) {
    const productUrl = productCard.dataset.productUrl;
    const variantLink = currentVariant.id ? `${productUrl}?variant=${currentVariant.id}` : productUrl;
    const productLinks = Array.from(productCard.querySelectorAll('[data-product-link]'));

    productLinks.forEach(link => link.href = variantLink);
  }

  /*
    This method updates the product card variant featured image to the current variant featured image
  */
  updateFeaturedImage(currentVariant, productCard) {
    const imageWrapper = productCard.querySelector('[data-image-wrapper] img');

    if (currentVariant.featured_image && imageWrapper) {
      imageWrapper.src = currentVariant.featured_image.src;
      imageWrapper.srcset = "";
    } else {
      imageWrapper.src = this.product.featured_image;
      imageWrapper.srcset = "";
    }
  }

  handleGroupCollectionSwatch() {
    const groupProductSwatches = this.querySelectorAll('[data-group-swatch]');
    if (!groupProductSwatches || groupProductSwatches.length <= 0) return;

    groupProductSwatches.forEach((productSwatch) => {
      productSwatch.addEventListener('click', (event) => {
        if (event.target.hasAttribute('data-product-url')) {
          this.productUrl = event.target.dataset.productUrl;
          this.updateGroupCollectionProduct(this.productUrl);
        }
      });
    })
  }

  async updateGroupCollectionProduct(productUrl) {
    this.productWrapper.classList.add('loading');

    const newGroupCollectionResponse = await fetch(productUrl);
    const newGroupCollectionText = await newGroupCollectionResponse.text();

    const parsedHtml = new DOMParser()
      .parseFromString(newGroupCollectionText, 'text/html');

    this.productWrapper.innerHTML = parsedHtml.querySelector('[data-product-wrapper]').innerHTML;
    this.productWrapper.classList.remove('loading');
  }
}

try {
  customElements.define('product-form', ProductFormElement);
}
catch(e) {}
